// Очередь неотправленных ответов «Моих задач» (030: T033; FR-030; research Р9; ASG-FLOW-07).
// Ответ («Выполнено», «Новый срок…», «Пояснение…») сначала ложится в очередь браузера, потом
// уходит на сервер: нет сети или сеанс закончился — он остаётся, строка помечена «Отправится при
// сети». Очередь уходит по событию online, при открытии экрана и после нового входа того же
// пользователя: записи хранятся по номеру пользователя, чужие этим входом не уходят. Повтор
// commandId сервер не применяет дважды (011); правка поля с другой версией — конфликт
// (REC-001, MIN-004): запись остаётся с пометкой «Пункт изменился, обновите и повторите».
// Выход очищает очередь только после вопроса, как отложенные правки (shell/session.ts).
//
// localStorage может не отвечать (приватное окно, запрет сайта): тогда очередь живёт только
// в памяти вкладки — экран работает, ответ без сети теряется вместе с вкладкой.
import type { MyAnswer, MyAnswerResult } from '@kp/contracts';
import { useCallback, useSyncExternalStore } from 'react';
import { NetworkError, isApiError } from '../../api/client.ts';

export interface OutboxEntry {
  commandId: string;
  /** Пункт ответа (uuid пункта в системе). */
  itemId: string;
  /** «3.2» — подпись в тостах и в строке. */
  number: string;
  /** Тело my.answer целиком: commandId, вид, значения и версия поля. */
  answer: MyAnswer;
  /** Когда человек ответил, ISO. */
  at: string;
  /** pending — ждёт сети или входа; conflict — пункт изменился, ответ не применён. */
  state: 'pending' | 'conflict';
}

const PREFIX = 'kp.my-items.outbox.';

/** Очередь в памяти — запасная, если localStorage не отвечает. */
const memory = new Map<string, OutboxEntry[]>();
/** Разобранная запись хранилища: тот же текст — тот же снимок (useSyncExternalStore). */
const parsed = new Map<string, { raw: string | null; entries: OutboxEntry[] }>();
const listeners = new Set<() => void>();
const NONE: OutboxEntry[] = [];

function keyOf(userId: string): string {
  return `${PREFIX}${userId}`;
}

function isEntry(value: unknown): value is OutboxEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Partial<OutboxEntry>;
  return (
    typeof entry.commandId === 'string' &&
    typeof entry.itemId === 'string' &&
    typeof entry.number === 'string' &&
    typeof entry.at === 'string' &&
    (entry.state === 'pending' || entry.state === 'conflict') &&
    typeof entry.answer === 'object' &&
    entry.answer !== null
  );
}

function read(userId: string): OutboxEntry[] {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(keyOf(userId));
  } catch {
    return memory.get(userId) ?? NONE;
  }
  const cached = parsed.get(userId);
  if (cached && cached.raw === raw) return cached.entries;
  let entries: OutboxEntry[];
  try {
    const value: unknown = raw ? JSON.parse(raw) : [];
    entries = Array.isArray(value) ? value.filter(isEntry) : NONE;
  } catch {
    entries = NONE;
  }
  parsed.set(userId, { raw, entries });
  return entries;
}

function write(userId: string, entries: OutboxEntry[]): void {
  try {
    if (entries.length === 0) window.localStorage.removeItem(keyOf(userId));
    else window.localStorage.setItem(keyOf(userId), JSON.stringify(entries));
  } catch {
    // Хранилище недоступно — очередь остаётся в памяти вкладки.
    memory.set(userId, entries);
  }
  for (const listener of listeners) listener();
}

/** Записи очереди пользователя по порядку ответов. */
export function outboxOf(userId: string): readonly OutboxEntry[] {
  return read(userId);
}

/** Сколько ответов пользователя ещё не отправлено — окно «Выйти и потерять правки?». */
export function outboxCount(userId: string): number {
  return read(userId).length;
}

export function putOutbox(userId: string, entry: OutboxEntry): void {
  write(userId, [...read(userId).filter((item) => item.commandId !== entry.commandId), entry]);
}

export function dropFromOutbox(userId: string, commandId: string): void {
  const entries = read(userId);
  if (!entries.some((item) => item.commandId === commandId)) return;
  write(
    userId,
    entries.filter((item) => item.commandId !== commandId),
  );
}

/** «Обновить» по пункту: его ответы с конфликтом убираются — их не применить. */
export function dropConflicts(userId: string, itemId: string): void {
  const entries = read(userId);
  const kept = entries.filter((item) => item.itemId !== itemId || item.state !== 'conflict');
  if (kept.length !== entries.length) write(userId, kept);
}

export function markOutboxConflict(userId: string, commandId: string): void {
  write(
    userId,
    read(userId).map((item) =>
      item.commandId === commandId ? { ...item, state: 'conflict' as const } : item,
    ),
  );
}

/** Выход подтверждён: неотправленные ответы пользователя удаляются. */
export function dropOutbox(userId: string): void {
  write(userId, []);
}

/** Чужая вкладка поменяла очередь: снимок перечитывается из хранилища. */
function onStorage(event: StorageEvent): void {
  if (event.key !== null && !event.key.startsWith(PREFIX)) return;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1 && typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
}

/** Очередь пользователя для экрана: меняется вместе с хранилищем, в том числе из другой вкладки. */
export function useOutbox(userId: string | null): readonly OutboxEntry[] {
  const snapshot = useCallback(() => (userId ? read(userId) : NONE), [userId]);
  return useSyncExternalStore(subscribe, snapshot, () => NONE);
}

/**
 * Итог отправки: sent — сервер принял; queued — нет сети, сеанс закончился или сервис недоступен,
 * ответ ждёт в очереди; conflict — пункт изменился; failed — сервер отказал (чужой пункт,
 * пункт удалён), ответ убран из очереди.
 */
export type SendOutcome =
  | { kind: 'sent'; result: MyAnswerResult }
  | { kind: 'queued'; error: unknown }
  | { kind: 'conflict'; error: unknown }
  | { kind: 'failed'; error: unknown };

/** Как поступить с ответом по ошибке отправки. */
export function outcomeOf(error: unknown): SendOutcome['kind'] {
  if (error instanceof NetworkError) return 'queued';
  if (isApiError(error, 'AUTH-004')) return 'queued';
  if (isApiError(error, 'REC-001') || isApiError(error, 'MIN-004')) return 'conflict';
  if (isApiError(error) && error.status >= 500) return 'queued';
  return 'failed';
}

/** Ответы, которые уходят прямо сейчас: двойной запуск (экран и online сразу) не шлёт дважды. */
const inFlight = new Set<string>();

/**
 * Отправить один ответ из очереди: итог — что стало с записью. Запись уже в очереди (putOutbox
 * до вызова): sent и failed её убирают, conflict помечает, queued оставляет.
 */
export async function sendEntry(
  userId: string,
  entry: OutboxEntry,
  send: (entry: OutboxEntry) => Promise<MyAnswerResult>,
): Promise<SendOutcome | null> {
  if (inFlight.has(entry.commandId)) return null;
  inFlight.add(entry.commandId);
  try {
    const result = await send(entry);
    dropFromOutbox(userId, entry.commandId);
    return { kind: 'sent', result };
  } catch (error) {
    const kind = outcomeOf(error);
    if (kind === 'failed') dropFromOutbox(userId, entry.commandId);
    if (kind === 'conflict') markOutboxConflict(userId, entry.commandId);
    return { kind, error } as SendOutcome;
  } finally {
    inFlight.delete(entry.commandId);
  }
}

/**
 * Отправить очередь пользователя по порядку ответов. Пункт с конфликтом не повторяется — его
 * решает человек («Обновить»). Нет сети — дальше не идём: следующие уйдут вместе с ним.
 */
export async function flushOutbox(
  userId: string,
  send: (entry: OutboxEntry) => Promise<MyAnswerResult>,
  onOutcome?: (entry: OutboxEntry, outcome: SendOutcome) => void,
): Promise<void> {
  for (const entry of [...read(userId)]) {
    if (entry.state !== 'pending') continue;
    const outcome = await sendEntry(userId, entry, send);
    if (!outcome) continue;
    onOutcome?.(entry, outcome);
    if (outcome.kind === 'queued') return;
  }
}
